import axios, {type AxiosInstance} from 'axios'
import {store} from '../../store'
import {errorOccured} from '../../store/state/auth/action-creators'

// Vessel VSAT/satellite links are high-latency; the prior 10s ceiling was the
// single highest-volume Sentry error ("timeout of 10000ms exceeded", ~190 users
// on /login alone). Tiered so large endpoints wait longer rather than fail —
// we'd rather a heavy query take a couple of minutes than time out on a slow
// link. DEFAULT is for the many small, latency-bound calls; EXTENDED/SUPER are
// passed per-call to the known-heavy ones (metrics, operational dashboard,
// inventory finds). See CDS-5845.
export const DEFAULT_TIMEOUT = 60000
export const EXTENDED_TIMEOUT = 90000
export const SUPER_EXTENDED_TIMEOUT = 120000

const EXPIRED_SESSION = 'Session expired'
const UNAUTHORIZED = 401

export const makeAxios = (): Promise<AxiosInstance> =>
    // load config
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    fetch(`/config/${import.meta.env.VITE_ALT_BACKEND ?? 'medulla-ui'}.json`)
        .then((response) => response.json())
        .then((json) =>
            // create axios
            axios.create({
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                baseURL: json.baseUrl,
                ...timeout(DEFAULT_TIMEOUT),
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
        )
        .then((axiosInstance) => {
            // interceptor to add auth token
            axiosInstance.interceptors.request.use((config) => {
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (config.headers) {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    config.headers.Authorization = `Bearer ${store.getState().auth.authToken}`
                }

                return config
            })

            // interceptor to handle FormData
            axiosInstance.interceptors.request.use((config) => {
                if (config.data instanceof FormData) {
                    config.headers['Content-Type'] = 'multipart/form-data'
                }

                return config
            })

            // interceptor to handle expired session
            axiosInstance.interceptors.response.use(
                (response) => response,
                (error) => {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if (error && error.response && error.response.status === UNAUTHORIZED) {
                        store.dispatch(errorOccured(EXPIRED_SESSION))

                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                        return Promise.reject(error)
                    } else {
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                        return Promise.reject(error)
                    }
                },
            )

            // interceptor to handle retry
            axiosInstance.interceptors.response.use(undefined, function axiosRetryInterceptor(err) {
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                const config = err.config
                // If config does not exist or the retry option is not set, reject
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (!config || !config.retry) {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    return Promise.reject(err)
                }

                // Set the variable for keeping track of the retry count
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                config.__retryCount = config.__retryCount || 0

                // Check if we've maxed out the total number of retries
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if (config.__retryCount >= config.retry) {
                    // Reject with the error
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    return Promise.reject(err)
                }

                // Create new promise to handle exponential backoff
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const backoffPeriod = Math.pow(2, config.__retryCount++) * (config.retryDelay || 1)
                const backoff = new Promise<void>(function (resolve) {
                    setTimeout(function () {
                        resolve()
                    }, backoffPeriod)
                })

                // Return the promise in which recalls axios to retry the request
                return backoff.then(function () {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return axiosInstance(config)
                })
            })

            return axiosInstance
        })

/**
 * Sets a timeout for both response related and network related requests
 * @param ms
 */
function timeout(ms: number): {timeout: number} {
    return {
        timeout: ms,
    }
}
